/**
 * A deterministic mock scenario, chosen by a `?<param>=` URL parameter inside
 * mock scope and remembered for the session - the way `?enforcement=` picks
 * readiness and `?deployment=` a deployment outcome. Specs and demos pin one
 * with `set`; `set(undefined)` clears the pin.
 *
 * Resolved once per distinct `location.search`, not per call: stores ask for
 * the scenario on every read.
 */
export function createScenarioSwitch<S extends string>({
  param,
  storageKey,
  values,
  fallback,
}: {
  param: string;
  storageKey: string;
  values: readonly S[];
  fallback: S;
}) {
  let pinned: S | undefined;
  let resolved: { search: string; scenario: S } | undefined;
  const is = (value: unknown): value is S => values.includes(value as S);

  const set = (scenario: S | undefined): void => {
    pinned = scenario;
    resolved = undefined;
    try {
      if (scenario) sessionStorage.setItem(storageKey, scenario);
      else sessionStorage.removeItem(storageKey);
    } catch {
      // Storage can be unavailable (private mode); the in-memory pin holds.
    }
  };

  const get = (): S => {
    if (pinned) return pinned;
    try {
      const { search } = window.location;
      if (resolved?.search === search) return resolved.scenario;
      const fromUrl = new URLSearchParams(search).get(param);
      let scenario = fallback;
      if (is(fromUrl)) {
        sessionStorage.setItem(storageKey, fromUrl);
        scenario = fromUrl;
      } else {
        const stored = sessionStorage.getItem(storageKey);
        if (is(stored)) scenario = stored;
      }
      resolved = { search, scenario };
      return scenario;
    } catch {
      return fallback;
    }
  };

  return { get, set };
}
